let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  TextInput,
  Modal,
  Pressable,
  Alert,
  Image,
  FlatList,
  Animated,
  StatusBar,
  Dimensions,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialIcons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  Feather,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// ** INTEGRATION: Enhanced Interview History Service **
const InterviewHistoryService = {
  async getAllInterviews() {
    try {
      console.log('🔄 Loading all interviews from storage...');
      
      // Get all interview keys
      const keys = await AsyncStorage.getAllKeys();
      const interviewKeys = keys.filter(key => key.startsWith('interview_'));
      
      if (interviewKeys.length === 0) {
        console.log('📝 No stored interviews found, using mock data');
        return this.getMockInterviews();
      }
      
      // Get all interview data
      const interviews = await AsyncStorage.multiGet(interviewKeys);
      const parsedInterviews = interviews
        .map(([key, value]) => value ? JSON.parse(value) : null)
        .filter(interview => interview !== null);
      
      console.log(`✅ Loaded ${parsedInterviews.length} interviews from storage`);
      return parsedInterviews;
      
    } catch (error) {
      console.error('❌ Error loading interviews:', error);
      return this.getMockInterviews();
    }
  },

  getMockInterviews() {
    return [
      {
        id: 'int_001',
        company: 'TechCorp London',
        position: 'Senior React Developer',
        type: 'Video Interview',
        date: '2025-01-28',
        time: '14:00',
        duration: '45 min',
        status: 'completed',
        outcome: 'successful',
        logo: { uri: 'https://via.placeholder.com/60/0e4e96/FFFFFF?text=TC' },
        location: 'Remote (Video Call)',
        aiMatchScore: 92,
        interviewers: [
          {
            name: 'Sarah Johnson',
            title: 'Technical Lead',
            avatar: null,
            linkedin: 'https://linkedin.com/in/sarahjohnson',
          },
          {
            name: 'Michael Chen',
            title: 'Engineering Manager',
            avatar: null,
            linkedin: 'https://linkedin.com/in/michaelchen',
          },
        ],
        feedback: {
          rating: 4,
          difficulty: 3,
          preparation: 4,
          feedback: 'Great technical knowledge and communication skills. Well prepared for the role.',
          improvements: 'Could improve on system design questions.',
          wouldRecommend: true,
        },
        notes: 'Discussed React performance optimization and state management patterns.',
        nextSteps: 'Waiting for final decision from hiring committee.',
        salary: '£65,000 - £85,000',
        benefits: ['Health Insurance', 'Pension', 'Flexible Working', '25 Days Holiday'],
      },
      {
        id: 'int_002',
        company: 'StartupHub Manchester',
        position: 'Frontend Engineer',
        type: 'Phone Interview',
        date: '2025-01-25',
        time: '10:30',
        duration: '30 min',
        status: 'completed',
        outcome: 'pending',
        logo: { uri: 'https://via.placeholder.com/60/10B981/FFFFFF?text=SH' },
        location: 'Phone Call',
        aiMatchScore: 85,
        interviewers: [
          {
            name: 'Emma Wilson',
            title: 'HR Manager',
            avatar: null,
            linkedin: 'https://linkedin.com/in/emmawilson',
          },
        ],
        feedback: {
          rating: 3,
          difficulty: 2,
          preparation: 3,
          feedback: 'Good cultural fit discussion. Positive about remote work experience.',
          improvements: 'Need to better articulate career goals.',
          wouldRecommend: true,
        },
        notes: 'Initial screening call. Discussed company culture and remote work policies.',
        nextSteps: 'Technical interview scheduled for next week.',
        salary: '£45,000 - £60,000',
        benefits: ['Stock Options', 'Remote Work', 'Learning Budget'],
      },
      {
        id: 'int_003',
        company: 'FinTech Solutions Edinburgh',
        position: 'Full Stack Developer',
        type: 'In-Person Interview',
        date: '2025-01-22',
        time: '11:00',
        duration: '60 min',
        status: 'completed',
        outcome: 'unsuccessful',
        logo: { uri: 'https://via.placeholder.com/60/8B5CF6/FFFFFF?text=FS' },
        location: 'Edinburgh Office',
        aiMatchScore: 78,
        interviewers: [
          {
            name: 'David Brown',
            title: 'CTO',
            avatar: null,
            linkedin: 'https://linkedin.com/in/davidbrown',
          },
          {
            name: 'Lisa Garcia',
            title: 'Senior Developer',
            avatar: null,
            linkedin: 'https://linkedin.com/in/lisagarcia',
          },
        ],
        feedback: {
          rating: 2,
          difficulty: 4,
          preparation: 2,
          feedback: 'Strong technical skills but lacked experience with financial systems.',
          improvements: 'Need more experience with backend technologies and financial domain knowledge.',
          wouldRecommend: false,
        },
        notes: 'Technical interview focused on financial algorithms and security.',
        nextSteps: 'Application unsuccessful. Feedback provided.',
        salary: '£400 - £600/day',
        benefits: ['Contract Role', 'Flexible Hours'],
      },
      {
        id: 'int_004',
        company: 'DesignStudio Pro Birmingham',
        position: 'UX/UI Designer',
        type: 'Portfolio Review',
        date: '2025-01-20',
        time: '15:30',
        duration: '90 min',
        status: 'completed',
        outcome: 'successful',
        logo: { uri: 'https://via.placeholder.com/60/F59E0B/FFFFFF?text=DS' },
        location: 'Birmingham Office',
        aiMatchScore: 88,
        interviewers: [
          {
            name: 'Anna Taylor',
            title: 'Design Director',
            avatar: null,
            linkedin: 'https://linkedin.com/in/annataylor',
          },
        ],
        feedback: {
          rating: 5,
          difficulty: 3,
          preparation: 5,
          feedback: 'Excellent portfolio presentation. Strong design thinking and user research skills.',
          improvements: 'Could expand knowledge of design systems.',
          wouldRecommend: true,
        },
        notes: 'Portfolio review went very well. Discussed design process and user research methods.',
        nextSteps: 'Offer extended. Negotiating start date.',
        salary: '£40,000 - £55,000',
        benefits: ['Creative Environment', 'Professional Development', 'Flexible Working'],
      },
      {
        id: 'int_005',
        company: 'CloudTech Systems',
        position: 'DevOps Engineer',
        type: 'Technical Assessment',
        date: '2025-02-05',
        time: '09:00',
        duration: '120 min',
        status: 'scheduled',
        outcome: 'pending',
        logo: { uri: 'https://via.placeholder.com/60/06B6D4/FFFFFF?text=CT' },
        location: 'Remote (Online Assessment)',
        aiMatchScore: 90,
        interviewers: [
          {
            name: 'Robert Kim',
            title: 'DevOps Lead',
            avatar: null,
            linkedin: 'https://linkedin.com/in/robertkim',
          },
        ],
        feedback: null,
        notes: 'Upcoming technical assessment covering AWS, Docker, and Kubernetes.',
        nextSteps: 'Complete technical assessment.',
        salary: '£55,000 - £70,000',
        benefits: ['Remote Work', 'AWS Certification', 'Stock Options'],
      },
      {
        id: 'int_006',
        company: 'InnovateLab Bristol',
        position: 'Product Manager',
        type: 'Panel Interview',
        date: '2025-01-15',
        time: '13:00',
        duration: '75 min',
        status: 'cancelled',
        outcome: 'cancelled',
        logo: { uri: 'https://via.placeholder.com/60/EF4444/FFFFFF?text=IL' },
        location: 'Bristol Office',
        aiMatchScore: 82,
        interviewers: [
          {
            name: 'Jennifer Lee',
            title: 'VP Product',
            avatar: null,
            linkedin: 'https://linkedin.com/in/jenniferlee',
          },
          {
            name: 'Mark Thompson',
            title: 'Engineering Director',
            avatar: null,
            linkedin: 'https://linkedin.com/in/markthompson',
          },
        ],
        feedback: null,
        notes: 'Interview cancelled due to internal restructuring.',
        nextSteps: 'Position on hold.',
        salary: '£60,000 - £75,000',
        benefits: ['Equity', 'Flexible Working', 'Learning Budget'],
      },
    ];
  },

  async saveFeedback(interviewId, feedbackData) {
    try {
      console.log('💾 Saving interview feedback:', { interviewId, feedbackData });
      
      const key = `interview_${interviewId}`;
      const existingData = await AsyncStorage.getItem(key);
      const interviewData = existingData ? JSON.parse(existingData) : {};
      
      const updatedData = {
        ...interviewData,
        feedback: feedbackData,
        lastUpdated: new Date().toISOString()
      };
      
      await AsyncStorage.setItem(key, JSON.stringify(updatedData));
      
      console.log('✅ Interview feedback saved successfully');
      return updatedData;
      
    } catch (error) {
      console.error('❌ Error saving feedback:', error);
      throw error;
    }
  },

  async deleteInterview(interviewId) {
    try {
      console.log('🗑️ Deleting interview:', interviewId);
      
      const key = `interview_${interviewId}`;
      await AsyncStorage.removeItem(key);
      
      // Also remove any related data
      const rescheduleKey = `reschedule_${interviewId}`;
      await AsyncStorage.removeItem(rescheduleKey);
      
      console.log('✅ Interview deleted successfully');
      
    } catch (error) {
      console.error('❌ Error deleting interview:', error);
      throw error;
    }
  }
};

// JOBENG Logo component
const JobengLogo = () => (
  <Image
    source={require('../../../assets/jobeng_icon.png')}
    style={{ width: 30, height: 30 }}
    resizeMode="contain"
  />
);

export default function InterviewHistoryScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_interviewhistoryscreen`;
    apiFetch('/api/data/interviews?sort=interview_date&order=desc&limit=50').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // ** INTEGRATION: Enhanced route params handling **
  const { 
    refreshData = false, 
    updatedInterviewId = null,
    highlightInterviewId = null,
    source = ''
  } = route?.params || {};

  // Main states
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Interview data states
  const [interviews, setInterviews] = useState([]);
  const [filteredInterviews, setFilteredInterviews] = useState([]);
  const [interviewStats, setInterviewStats] = useState({});
  
  // Modal states
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    dateRange: 'all',
    company: 'all',
    outcome: 'all',
  });
  
  // Feedback states
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 0,
    difficulty: 0,
    preparation: 0,
    feedback: '',
    improvements: '',
    wouldRecommend: null,
  });

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.9)).current;

  // Refs
  const scrollViewRef = useRef();

  // Load data on component mount and focus
  useEffect(() => {
    initializeScreen();
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      if (refreshData) {
        console.log('🔄 Refreshing interview history data...');
        loadInterviewData();
      }
    }, [refreshData])
  );

  // ** INTEGRATION: Enhanced initialization **
  const initializeScreen = async () => {
    try {
      setLoading(true);
      
      // Load interview data
      await loadInterviewData();
      
      // Start animations
      startAnimations();
      
      // Log source information
      if (source) {
        console.log(`🔗 Navigated from ${source} to InterviewHistory`);
      }
      
      // Handle highlight if specified
      if (highlightInterviewId) {
        console.log(`✨ Highlighting interview: ${highlightInterviewId}`);
        // Could scroll to and highlight specific interview
      }
      
    } catch (error) {
      console.error('❌ Error initializing interview history screen:', error);
    } finally {
      setLoading(false);
    }
  };

  // ** INTEGRATION: Enhanced data loading **
  const loadInterviewData = async () => {
    try {
      console.log('🔄 Loading interview history data...');
      
      // Load interviews from service
      const interviewData = await InterviewHistoryService.getAllInterviews();
      
      // Ensure unique IDs
      const validatedData = interviewData.map((item, index) => ({
        ...item,
        id: item.id || `interview_${index}_${Date.now()}`
      }));
      
      setInterviews(validatedData);
      setFilteredInterviews(validatedData);
      
      // Calculate statistics
      calculateInterviewStats(validatedData);
      
      console.log('✅ Interview history data loaded');
      
    } catch (error) {
      console.error('❌ Error loading interview data:', error);
    }
  };

  const calculateInterviewStats = (interviewData) => {
    const stats = {
      total: interviewData.length,
      completed: interviewData.filter(i => i.status === 'completed').length,
      scheduled: interviewData.filter(i => i.status === 'scheduled').length,
      cancelled: interviewData.filter(i => i.status === 'cancelled').length,
      successful: interviewData.filter(i => i.outcome === 'successful').length,
      unsuccessful: interviewData.filter(i => i.outcome === 'unsuccessful').length,
      pending: interviewData.filter(i => i.outcome === 'pending').length,
      averageRating: 0,
      averageMatchScore: 0,
      topCompanies: [],
      interviewTypes: {},
    };

    // Calculate averages
    const completedWithFeedback = interviewData.filter(i => i.feedback && i.feedback.rating);
    if (completedWithFeedback.length > 0) {
      stats.averageRating = completedWithFeedback.reduce((sum, i) => sum + i.feedback.rating, 0) / completedWithFeedback.length;
    }

    if (interviewData.length > 0) {
      stats.averageMatchScore = interviewData.reduce((sum, i) => sum + i.aiMatchScore, 0) / interviewData.length;
    }

    // Calculate success rate
    const completedInterviews = stats.completed;
    stats.successRate = completedInterviews > 0 ? (stats.successful / completedInterviews) * 100 : 0;

    // Top companies by interview count
    const companyCount = {};
    interviewData.forEach(interview => {
      companyCount[interview.company] = (companyCount[interview.company] || 0) + 1;
    });
    stats.topCompanies = Object.entries(companyCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([company, count]) => ({ company, count }));

    // Interview types distribution
    const typeCount = {};
    interviewData.forEach(interview => {
      typeCount[interview.type] = (typeCount[interview.type] || 0) + 1;
    });
    stats.interviewTypes = typeCount;

    setInterviewStats(stats);
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // ** INTEGRATION: Enhanced refresh **
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInterviewData();
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Filter interviews based on active tab and search
  useEffect(() => {
    let filtered = interviews;

    // Filter by tab
    if (activeTab !== 'all') {
      if (activeTab === 'upcoming') {
        filtered = filtered.filter(i => i.status === 'scheduled');
      } else if (activeTab === 'completed') {
        filtered = filtered.filter(i => i.status === 'completed');
      } else if (activeTab === 'successful') {
        filtered = filtered.filter(i => i.outcome === 'successful');
      } else if (activeTab === 'pending') {
        filtered = filtered.filter(i => i.outcome === 'pending');
      }
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(interview =>
        String(interview.company ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(interview.position ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(interview.type ?? '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply additional filters
    if (filters.status !== 'all') {
      filtered = filtered.filter(i => i.status === filters.status);
    }
    if (filters.type !== 'all') {
      filtered = filtered.filter(i => i.type === filters.type);
    }
    if (filters.outcome !== 'all') {
      filtered = filtered.filter(i => i.outcome === filters.outcome);
    }

    setFilteredInterviews(filtered);
  }, [interviews, activeTab, searchQuery, filters]);

  // ** INTEGRATION: Enhanced navigation to interview details **
  const navigateToInterviewDetails = (interview) => {
    console.log('🔗 Navigating to InterviewDetails:', interview.id);
    
    // Multiple navigation attempts for robustness
    const screenNames = ['InterviewDetails', 'InterviewDetailsScreen', 'InterviewDetail'];
    
    for (const screenName of screenNames) {
      try {
        navigation.navigate(screenName, {
          interview: interview,
          interviewId: interview.id,
          id: interview.id,
          source: 'InterviewHistory',
          highlightInterview: interview.id === highlightInterviewId
        });
        console.log(`✅ Successfully navigated to ${screenName}`);
        return;
      } catch (error) {
        console.warn(`⚠️ Failed to navigate to ${screenName}:`, error.message);
      }
    }
    
    // Fallback alert
    Alert.alert(
      'Navigation Error',
      'Unable to open interview details. Please try again.',
      [
        { text: 'OK' },
        {
          text: 'Debug Info',
          onPress: () => {
            Alert.alert('Debug Info', `Interview ID: ${interview.id}\nCompany: ${interview.company}\nPosition: ${interview.position}`);
          }
        }
      ]
    );
  };

  // ** INTEGRATION: Enhanced feedback handling **
  const handleProvideFeedback = (interview) => {
    setSelectedInterview(interview);
    
    // Pre-fill existing feedback if available
    if (interview.feedback) {
      setFeedbackForm({
        rating: interview.feedback.rating || 0,
        difficulty: interview.feedback.difficulty || 0,
        preparation: interview.feedback.preparation || 0,
        feedback: interview.feedback.feedback || '',
        improvements: interview.feedback.improvements || '',
        wouldRecommend: interview.feedback.wouldRecommend || null,
      });
    } else {
      // Reset form for new feedback
      setFeedbackForm({
        rating: 0,
        difficulty: 0,
        preparation: 0,
        feedback: '',
        improvements: '',
        wouldRecommend: null,
      });
    }
    
    setShowFeedbackModal(true);
  };

  // ** INTEGRATION: Enhanced feedback submission **
  const handleSubmitFeedback = async () => {
    try {
      if (!selectedInterview) return;
      
      if (feedbackForm.rating === 0) {
        Alert.alert('Missing Rating', 'Please provide an overall rating for the interview.');
        return;
      }
      
      console.log('💾 Submitting feedback for interview:', selectedInterview.id);
      
      // Save feedback through service
      await InterviewHistoryService.saveFeedback(selectedInterview.id, feedbackForm);
      
      // Update local state
      const updatedInterviews = interviews.map(interview => 
        interview.id === selectedInterview.id 
          ? { ...interview, feedback: feedbackForm }
          : interview
      );
      
      setInterviews(updatedInterviews);
      setShowFeedbackModal(false);
      
      Alert.alert(
        'Feedback Submitted',
        'Thank you for your feedback! This helps improve our interview matching.',
        [{ text: 'OK' }]
      );
      
    } catch (error) {
      console.error('❌ Error submitting feedback:', error);
      Alert.alert('Error', 'Failed to submit feedback. Please try again.');
    }
  };

  // ** INTEGRATION: Enhanced delete interview **
  const handleDeleteInterview = (interview) => {
    Alert.alert(
      'Delete Interview',
      `Are you sure you want to delete the interview with ${interview.company}? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await InterviewHistoryService.deleteInterview(interview.id);
              
              // Update local state
              const updatedInterviews = interviews.filter(i => i.id !== interview.id);
              setInterviews(updatedInterviews);
              
              Alert.alert('Deleted', 'Interview has been deleted successfully.');
              
            } catch (error) {
              console.error('❌ Error deleting interview:', error);
              Alert.alert('Error', 'Failed to delete interview. Please try again.');
            }
          }
        }
      ]
    );
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#10B981';
      case 'completed': return '#0e4e96';
      case 'cancelled': return '#EF4444';
      case 'reschedule_requested': return '#F59E0B';
      default: return '#64748B';
    }
  };

  // Get outcome color
  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'successful': return '#10B981';
      case 'unsuccessful': return '#EF4444';
      case 'pending': return '#F59E0B';
      case 'cancelled': return '#64748B';
      default: return '#64748B';
    }
  };

  // Render interview item
  const renderInterviewItem = ({ item, index }) => {
    const isHighlighted = item.id === highlightInterviewId;
    
    return (
      <Animated.View
        style={[
          styles.interviewItem,
          isHighlighted && styles.highlightedItem,
          {
            opacity: fadeAnim,
            transform: [
              { translateY: slideAnim },
              { scale: scaleAnim }
            ]
          }
        ]}
      >
        <TouchableOpacity
          style={styles.interviewContent}
          onPress={() => navigateToInterviewDetails(item)}
          activeOpacity={0.7}
        >
          {/* Company Logo and Basic Info */}
          <View style={styles.interviewHeader}>
            <Image source={item.logo} style={styles.companyLogo} />
            <View style={styles.interviewInfo}>
              <Text style={styles.companyName}>{item.company}</Text>
              <Text style={styles.positionName}>{item.position}</Text>
              <Text style={styles.interviewType}>{item.type}</Text>
            </View>
            <View style={styles.interviewMeta}>
              <Text style={styles.aiMatchScore}>{item.aiMatchScore}%</Text>
              <Text style={styles.aiMatchLabel}>Match</Text>
            </View>
          </View>

          {/* Interview Details */}
          <View style={styles.interviewDetails}>
            <View style={styles.detailRow}>
              <FontAwesome5 name="calendar-alt" size={14} color="#64748B" />
              <Text style={styles.detailText}>{item.date} at {item.time}</Text>
            </View>
            <View style={styles.detailRow}>
              <Ionicons name="location-outline" size={14} color="#64748B" />
              <Text style={styles.detailText}>{item.location}</Text>
            </View>
          </View>

          {/* Status and Outcome */}
          <View style={styles.statusRow}>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
              <Text style={styles.statusText}>
                {item.status === 'scheduled' ? 'Upcoming' : 
                 item.status === 'completed' ? 'Completed' :
                 item.status === 'cancelled' ? 'Cancelled' :
                 item.status === 'reschedule_requested' ? 'Reschedule Requested' :
                 item.status}
              </Text>
            </View>
            
            {item.outcome && item.outcome !== 'pending' && (
              <View style={[styles.outcomeBadge, { backgroundColor: getOutcomeColor(item.outcome) }]}>
                <Text style={styles.outcomeText}>
                  {item.outcome === 'successful' ? 'Success' :
                   item.outcome === 'unsuccessful' ? 'Unsuccessful' :
                   item.outcome}
                </Text>
              </View>
            )}
          </View>

          {/* Feedback Rating */}
          {item.feedback && item.feedback.rating > 0 && (
            <View style={styles.feedbackRow}>
              <Text style={styles.feedbackLabel}>Your Rating:</Text>
              <View style={styles.starsContainer}>
                {[1, 2, 3, 4, 5].map(star => (
                  <Ionicons
                    key={star}
                    name={star <= item.feedback.rating ? 'star' : 'star-outline'}
                    size={16}
                    color="#F59E0B"
                  />
                ))}
              </View>
            </View>
          )}
        </TouchableOpacity>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          {item.status === 'completed' && !item.feedback && (
            <TouchableOpacity
              style={[styles.actionButton, styles.feedbackButton]}
              onPress={() => handleProvideFeedback(item)}
            >
              <MaterialIcons name="rate-review" size={16} color="#0e4e96" />
              <Text style={styles.actionButtonText}>Feedback</Text>
            </TouchableOpacity>
          )}
          
          {item.status === 'scheduled' && (
            <TouchableOpacity
              style={[styles.actionButton, styles.rescheduleButton]}
              onPress={() => {
                navigation.navigate('RescheduleInterview', {
                  interview: item,
                  source: 'InterviewHistory'
                });
              }}
            >
              <MaterialIcons name="schedule" size={16} color="#F59E0B" />
              <Text style={[styles.actionButtonText, { color: '#F59E0B' }]}>Reschedule</Text>
            </TouchableOpacity>
          )}
          
          <TouchableOpacity
            style={[styles.actionButton, styles.deleteButton]}
            onPress={() => handleDeleteInterview(item)}
          >
            <MaterialIcons name="delete-outline" size={16} color="#EF4444" />
            <Text style={[styles.actionButtonText, { color: '#EF4444' }]}>Delete</Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    );
  };

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="calendar-clock" size={80} color="#CBD5E1" />
      <Text style={styles.emptyStateTitle}>No Interview History</Text>
      <Text style={styles.emptyStateMessage}>
        Your completed and scheduled interviews will appear here.
      </Text>
      <TouchableOpacity
        style={styles.emptyStateButton}
        onPress={() => navigation.navigate('TestsInterviews')}
      >
        <Text style={styles.emptyStateButtonText}>Find Interviews</Text>
      </TouchableOpacity>
    </View>
  );

  // Render loading state
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Interview History</Text>
          <View style={{ width: 32 }}>{/* Empty view for balance */}</View>
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading interview history...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Interview History</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerAction}
            onPress={() => setShowAnalyticsModal(true)}
          >
            <MaterialIcons name="analytics" size={24} color="#ffffff" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.headerAction}
            onPress={() => setShowFilterModal(true)}
          >
            <MaterialIcons name="filter-list" size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Source indicator */}
      {source && (
        <View style={styles.sourceIndicator}>
          <Ionicons name="information-circle-outline" size={16} color="#007AFF" />
          <Text style={styles.sourceText}>From {source}</Text>
        </View>
      )}

      {/* Statistics Cards */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.statsScrollView}
        contentContainerStyle={styles.statsContainer}
      >
        <View key="total" style={styles.statCard}>
          <Text style={styles.statNumber}>{interviewStats.total || 0}</Text>
          <Text style={styles.statLabel}>Total</Text>
        </View>
        <View key="completed" style={styles.statCard}>
          <Text style={styles.statNumber}>{interviewStats.completed || 0}</Text>
          <Text style={styles.statLabel}>Completed</Text>
        </View>
        <View key="successful" style={styles.statCard}>
          <Text style={styles.statNumber}>{interviewStats.successful || 0}</Text>
          <Text style={styles.statLabel}>Successful</Text>
        </View>
        <View key="successRate" style={styles.statCard}>
          <Text style={styles.statNumber}>{Math.round(interviewStats.successRate || 0)}%</Text>
          <Text style={styles.statLabel}>Success Rate</Text>
        </View>
        <View key="avgMatch" style={styles.statCard}>
          <Text style={styles.statNumber}>{Math.round(interviewStats.averageMatchScore || 0)}%</Text>
          <Text style={styles.statLabel}>Avg Match</Text>
        </View>
      </ScrollView>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <Ionicons name="search" size={20} color="#64748B" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search interviews..."
            placeholderTextColor="#94A3B8"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Filter Tabs */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.tabsScrollView}
        contentContainerStyle={styles.tabsContainer}
      >
        {[
          { key: 'all', label: 'All', count: interviewStats.total },
          { key: 'upcoming', label: 'Upcoming', count: interviewStats.scheduled },
          { key: 'completed', label: 'Completed', count: interviewStats.completed },
          { key: 'successful', label: 'Successful', count: interviewStats.successful },
          { key: 'pending', label: 'Pending', count: interviewStats.pending },
        ].map(tab => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.label}
            </Text>
            {tab.count > 0 && (
              <View style={[
                styles.tabBadge,
                activeTab === tab.key && styles.activeTabBadge
              ]}>
                <Text style={[
                  styles.tabBadgeText,
                  activeTab === tab.key && styles.activeTabBadgeText
                ]}>
                  {tab.count}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Interview List */}
      <FlatList
        ref={scrollViewRef}
        data={filteredInterviews}
        renderItem={renderInterviewItem}
        keyExtractor={(item, index) => item.id ? item.id.toString() : `interview-${index}`}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#0e4e96']}
            tintColor="#0e4e96"
          />
        }
        ListEmptyComponent={renderEmptyState}
      />

      {/* Feedback Modal */}
      <Modal
        visible={showFeedbackModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowFeedbackModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.feedbackModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Interview Feedback</Text>
              <TouchableOpacity onPress={() => setShowFeedbackModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.feedbackForm}>
              {selectedInterview && (
                <View style={styles.interviewSummary}>
                  <Text style={styles.summaryTitle}>
                    {selectedInterview.company} - {selectedInterview.position}
                  </Text>
                  <Text style={styles.summaryDate}>
                    {selectedInterview.date} at {selectedInterview.time}
                  </Text>
                </View>
              )}

              {/* Overall Rating */}
              <View style={styles.formSection}>
                <Text style={styles.formLabel}>Overall Rating *</Text>
                <View style={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <TouchableOpacity
                      key={star}
                      onPress={() => setFeedbackForm(prev => ({ ...prev, rating: star }))}
                    >
                      <Ionicons
                        name={star <= feedbackForm.rating ? 'star' : 'star-outline'}
                        size={32}
                        color="#F59E0B"
                      />
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Difficulty Rating */}
              <View style={styles.formSection}>
                <Text style={styles.formLabel}>Interview Difficulty</Text>
                <View style={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map(level => (
                    <TouchableOpacity
                      key={level}
                      style={[
                        styles.difficultyButton,
                        feedbackForm.difficulty === level && styles.selectedDifficulty
                      ]}
                      onPress={() => setFeedbackForm(prev => ({ ...prev, difficulty: level }))}
                    >
                      <Text style={[
                        styles.difficultyText,
                        feedbackForm.difficulty === level && styles.selectedDifficultyText
                      ]}>
                        {level}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <Text style={styles.ratingHint}>1 = Very Easy, 5 = Very Hard</Text>
              </View>

              {/* Preparation Rating */}
              <View style={styles.formSection}>
                <Text style={styles.formLabel}>How well prepared were you?</Text>
                <View style={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map(level => (
                    <TouchableOpacity
                      key={level}
                      style={[
                        styles.difficultyButton,
                        feedbackForm.preparation === level && styles.selectedDifficulty
                      ]}
                      onPress={() => setFeedbackForm(prev => ({ ...prev, preparation: level }))}
                    >
                      <Text style={[
                        styles.difficultyText,
                        feedbackForm.preparation === level && styles.selectedDifficultyText
                      ]}>
                        {level}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <Text style={styles.ratingHint}>1 = Not Prepared, 5 = Very Well Prepared</Text>
              </View>

              {/* Feedback Text */}
              <View style={styles.formSection}>
                <Text style={styles.formLabel}>Your Experience</Text>
                <TextInput
                  style={styles.textInput}
                  placeholder="Share your interview experience..."
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={4}
                  value={feedbackForm.feedback}
                  onChangeText={text => setFeedbackForm(prev => ({ ...prev, feedback: text }))}
                />
              </View>

              {/* Improvements */}
              <View style={styles.formSection}>
                <Text style={styles.formLabel}>Areas for Improvement</Text>
                <TextInput
                  style={styles.textInput}
                  placeholder="What could you improve for next time?"
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={3}
                  value={feedbackForm.improvements}
                  onChangeText={text => setFeedbackForm(prev => ({ ...prev, improvements: text }))}
                />
              </View>

              {/* Recommendation */}
              <View style={styles.formSection}>
                <Text style={styles.formLabel}>Would you recommend this company?</Text>
                <View style={styles.recommendationContainer}>
                  <TouchableOpacity
                    style={[
                      styles.recommendationButton,
                      feedbackForm.wouldRecommend === true && styles.selectedRecommendation
                    ]}
                    onPress={() => setFeedbackForm(prev => ({ ...prev, wouldRecommend: true }))}
                  >
                    <Ionicons name="thumbs-up" size={20} color={feedbackForm.wouldRecommend === true ? '#ffffff' : '#10B981'} />
                    <Text style={[
                      styles.recommendationText,
                      feedbackForm.wouldRecommend === true && styles.selectedRecommendationText
                    ]}>
                      Yes
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.recommendationButton,
                      feedbackForm.wouldRecommend === false && styles.selectedRecommendation
                    ]}
                    onPress={() => setFeedbackForm(prev => ({ ...prev, wouldRecommend: false }))}
                  >
                    <Ionicons name="thumbs-down" size={20} color={feedbackForm.wouldRecommend === false ? '#ffffff' : '#EF4444'} />
                    <Text style={[
                      styles.recommendationText,
                      feedbackForm.wouldRecommend === false && styles.selectedRecommendationText
                    ]}>
                      No
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </ScrollView>

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowFeedbackModal(false)}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.submitButton}
                onPress={handleSubmitFeedback}
              >
                <Text style={styles.submitButtonText}>Submit Feedback</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Analytics Modal */}
      <Modal
        visible={showAnalyticsModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowAnalyticsModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.analyticsModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Interview Analytics</Text>
              <TouchableOpacity onPress={() => setShowAnalyticsModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.analyticsContent}>
              {/* Success Rate */}
              <View style={styles.analyticsSection}>
                <Text style={styles.analyticsSectionTitle}>Success Rate</Text>
                <View style={styles.successRateContainer}>
                  <Text style={styles.successRateNumber}>
                    {Math.round(interviewStats.successRate || 0)}%
                  </Text>
                  <Text style={styles.successRateLabel}>
                    {interviewStats.successful || 0} successful out of {interviewStats.completed || 0} completed
                  </Text>
                </View>
              </View>

              {/* Average Ratings */}
              <View style={styles.analyticsSection}>
                <Text style={styles.analyticsSectionTitle}>Average Ratings</Text>
                <View style={styles.ratingsGrid}>
                  <View style={styles.ratingItem}>
                    <Text style={styles.ratingValue}>
                      {(interviewStats.averageRating || 0).toFixed(1)}
                    </Text>
                    <Text style={styles.ratingLabel}>Overall</Text>
                  </View>
                  <View style={styles.ratingItem}>
                    <Text style={styles.ratingValue}>
                      {Math.round(interviewStats.averageMatchScore || 0)}%
                    </Text>
                    <Text style={styles.ratingLabel}>AI Match</Text>
                  </View>
                </View>
              </View>

              {/* Top Companies */}
              {interviewStats.topCompanies && interviewStats.topCompanies.length > 0 && (
                <View style={styles.analyticsSection}>
                  <Text style={styles.analyticsSectionTitle}>Top Companies</Text>
                  {interviewStats.topCompanies.map((company, index) => (
                    <View key={index} style={styles.companyItem}>
                      <Text style={styles.companyName}>{company.company}</Text>
                      <Text style={styles.companyCount}>{company.count} interviews</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Interview Types */}
              {interviewStats.interviewTypes && Object.keys(interviewStats.interviewTypes).length > 0 && (
                <View style={styles.analyticsSection}>
                  <Text style={styles.analyticsSectionTitle}>Interview Types</Text>
                  {Object.entries(interviewStats.interviewTypes).map(([type, count]) => (
                    <View key={type} style={styles.typeItem}>
                      <Text style={styles.typeName}>{type}</Text>
                      <Text style={styles.typeCount}>{count}</Text>
                    </View>
                  ))}
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Filter Modal */}
      <Modal
        visible={showFilterModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowFilterModal(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.filterModalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Filter Interviews</Text>
              <TouchableOpacity onPress={() => setShowFilterModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.filterContent}>
              {/* Status Filter */}
              <View style={styles.filterSection}>
                <Text style={styles.filterSectionTitle}>Status</Text>
                <View style={styles.filterOptions}>
                  {['all', 'scheduled', 'completed', 'cancelled'].map(status => (
                    <TouchableOpacity
                      key={status}
                      style={[
                        styles.filterOption,
                        filters.status === status && styles.selectedFilterOption
                      ]}
                      onPress={() => setFilters(prev => ({ ...prev, status }))}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        filters.status === status && styles.selectedFilterOptionText
                      ]}>
                        {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Type Filter */}
              <View style={styles.filterSection}>
                <Text style={styles.filterSectionTitle}>Interview Type</Text>
                <View style={styles.filterOptions}>
                  {['all', 'Video Interview', 'Phone Interview', 'In-Person Interview', 'Technical Assessment', 'Portfolio Review', 'Panel Interview'].map(type => (
                    <TouchableOpacity
                      key={type}
                      style={[
                        styles.filterOption,
                        filters.type === type && styles.selectedFilterOption
                      ]}
                      onPress={() => setFilters(prev => ({ ...prev, type }))}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        filters.type === type && styles.selectedFilterOptionText
                      ]}>
                        {type === 'all' ? 'All Types' : type}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>

              {/* Outcome Filter */}
              <View style={styles.filterSection}>
                <Text style={styles.filterSectionTitle}>Outcome</Text>
                <View style={styles.filterOptions}>
                  {['all', 'successful', 'unsuccessful', 'pending'].map(outcome => (
                    <TouchableOpacity
                      key={outcome}
                      style={[
                        styles.filterOption,
                        filters.outcome === outcome && styles.selectedFilterOption
                      ]}
                      onPress={() => setFilters(prev => ({ ...prev, outcome }))}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        filters.outcome === outcome && styles.selectedFilterOptionText
                      ]}>
                        {outcome === 'all' ? 'All' : outcome.charAt(0).toUpperCase() + outcome.slice(1)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>

            <View style={styles.filterActions}>
              <TouchableOpacity
                style={styles.clearFiltersButton}
                onPress={() => {
                  setFilters({
                    status: 'all',
                    type: 'all',
                    dateRange: 'all',
                    company: 'all',
                    outcome: 'all',
                  });
                }}
              >
                <Text style={styles.clearFiltersText}>Clear All</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.applyFiltersButton}
                onPress={() => setShowFilterModal(false)}
              >
                <Text style={styles.applyFiltersText}>Apply Filters</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  headerAction: {
    padding: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
  },
  sourceIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f8ff',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 8,
    gap: 8,
  },
  sourceText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  statsScrollView: {
    marginTop: 8,
  },
  statsContainer: {
    paddingHorizontal: 16,
    gap: 12,
  },
  statCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    minWidth: 80,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0e4e96',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
  },
  tabsScrollView: {
    marginBottom: 8,
  },
  tabsContainer: {
    paddingHorizontal: 16,
    gap: 8,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 6,
  },
  activeTab: {
    backgroundColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabBadge: {
    backgroundColor: '#E2E8F0',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
  },
  activeTabBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
  },
  activeTabBadgeText: {
    color: '#ffffff',
  },
  listContainer: {
    padding: 16,
  },
  interviewItem: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  highlightedItem: {
    borderWidth: 2,
    borderColor: '#007AFF',
    shadowColor: '#007AFF',
    shadowOpacity: 0.2,
  },
  interviewContent: {
    padding: 16,
  },
  interviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  companyLogo: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12,
  },
  interviewInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
  },
  positionName: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 2,
  },
  interviewType: {
    fontSize: 12,
    color: '#94A3B8',
  },
  interviewMeta: {
    alignItems: 'center',
  },
  aiMatchScore: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0e4e96',
  },
  aiMatchLabel: {
    fontSize: 10,
    color: '#64748B',
  },
  interviewDetails: {
    marginBottom: 12,
    gap: 6,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    fontSize: 13,
    color: '#64748B',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#ffffff',
  },
  outcomeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  outcomeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#ffffff',
  },
  feedbackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  feedbackLabel: {
    fontSize: 12,
    color: '#64748B',
  },
  starsContainer: {
    flexDirection: 'row',
    gap: 2,
  },
  actionButtons: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    paddingTop: 12,
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    gap: 4,
  },
  feedbackButton: {
    borderColor: '#0e4e96',
    backgroundColor: '#ffffff',
  },
  rescheduleButton: {
    borderColor: '#F59E0B',
    backgroundColor: '#ffffff',
  },
  deleteButton: {
    borderColor: '#EF4444',
    backgroundColor: '#ffffff',
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#0e4e96',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1E293B',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateMessage: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  emptyStateButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyStateButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  feedbackModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
  },
  analyticsModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
  },
  filterModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1E293B',
  },
  feedbackForm: {
    padding: 20,
  },
  interviewSummary: {
    backgroundColor: '#F8FAFC',
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 4,
  },
  summaryDate: {
    fontSize: 14,
    color: '#64748B',
  },
  formSection: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1E293B',
    marginBottom: 12,
  },
  ratingContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  difficultyButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedDifficulty: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  difficultyText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
  },
  selectedDifficultyText: {
    color: '#ffffff',
  },
  ratingHint: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 4,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: '#1E293B',
    textAlignVertical: 'top',
  },
  recommendationContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  recommendationButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    gap: 8,
  },
  selectedRecommendation: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  recommendationText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  selectedRecommendationText: {
    color: '#ffffff',
  },
  modalActions: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  submitButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  analyticsContent: {
    padding: 20,
  },
  analyticsSection: {
    marginBottom: 24,
  },
  analyticsSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 12,
  },
  successRateContainer: {
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    padding: 20,
    borderRadius: 8,
  },
  successRateNumber: {
    fontSize: 32,
    fontWeight: '700',
    color: '#0e4e96',
    marginBottom: 4,
  },
  successRateLabel: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
  },
  ratingsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  ratingItem: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  ratingValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0e4e96',
    marginBottom: 4,
  },
  ratingLabel: {
    fontSize: 12,
    color: '#64748B',
  },
  companyItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  companyName: {
    fontSize: 14,
    color: '#1E293B',
  },
  companyCount: {
    fontSize: 12,
    color: '#64748B',
  },
  typeItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  typeName: {
    fontSize: 14,
    color: '#1E293B',
  },
  typeCount: {
    fontSize: 12,
    color: '#64748B',
  },
  filterContent: {
    padding: 20,
  },
  filterSection: {
    marginBottom: 24,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#ffffff',
  },
  selectedFilterOption: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
  },
  selectedFilterOptionText: {
    color: '#ffffff',
  },
  filterActions: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    gap: 12,
  },
  clearFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    alignItems: 'center',
  },
  clearFiltersText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  applyFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
  },
  applyFiltersText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
});
